import React, { useState, useEffect, useReducer, useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Grid
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import PaymentIcon from "@mui/icons-material/Payment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PersonIcon from "@mui/icons-material/Person";
import DevicesIcon from "@mui/icons-material/Devices";
import QueueIcon from "@mui/icons-material/Queue";
import MoneyIcon from "@mui/icons-material/Money";
import DateRangeIcon from "@mui/icons-material/DateRange";

import SubscriptionModal from "../../components/SubscriptionModal";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import moment from "moment";

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const invoices = action.payload;
    const newUsers = [];
    invoices.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });
    return [...state, ...newUsers];
  }
  if (action.type === "RESET") {
    return [];
  }
  return state;
};

const Invoices = () => {
  const themeV4 = useThemeV4();
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [invoices, dispatch] = useReducer(reducer, []);
  const [storagePlans, setStoragePlans] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [companyPlan, setCompanyPlan] = useState(null);

  const handleOpenContactModal = (invoice) => {
    const invoiceWithPlanValue = {
      ...invoice,
      value: companyPlan && companyPlan.amount ? parseFloat(companyPlan.amount) : invoice.value
    };
    setStoragePlans(invoiceWithPlanValue);
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const fetchCompanyPlan = async () => {
      try {
        if (user && user.companyId) {
          const companyResponse = await api.get(`/companies/${user.companyId}`);
          const company = companyResponse.data;
          if (company && company.planId) {
            const planResponse = await api.get(`/plans/${company.planId}`);
            setCompanyPlan(planResponse.data);
          }
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchCompanyPlan();
  }, [user]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchInvoices = async () => {
        try {
          const { data } = await api.get("/invoices/all", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_INVOICES", payload: data });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchInvoices();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  const isLoadingFallback = !user || !user.companyId || !companyPlan;

  if (isLoadingFallback) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "#f8fafc" }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prevState) => prevState + 1);
    }
  };

  const getInvoiceStatus = (record) => {
    const hoje = moment(moment()).format("DD/MM/yyyy");
    const vencimento = moment(record.dueDate).format("DD/MM/yyyy");
    var diff = moment(vencimento, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"));
    var dias = moment.duration(diff).asDays();
    const status = record.status;
    
    if (status === "paid") {
      return { 
        text: "Pago", 
        color: "#10b981", 
        bgcolor: "rgba(16, 185, 129, 0.1)", 
        icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> 
      };
    }
    if (dias < 0) {
      return { 
        text: "Vencido", 
        color: "#ef4444", 
        bgcolor: "rgba(239, 68, 68, 0.1)", 
        icon: <ErrorIcon sx={{ fontSize: 16 }} /> 
      };
    } else {
      return { 
        text: "Em Aberto", 
        color: "#f59e0b", 
        bgcolor: "rgba(245, 158, 11, 0.1)", 
        icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} /> 
      };
    }
  };

  const renderDaysLeft = (record) => {
    const hoje = moment(moment()).format("DD/MM/yyyy");
    const vencimento = moment(record.dueDate).format("DD/MM/yyyy");
    var diff = moment(vencimento, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"));
    var dias = moment.duration(diff).asDays();
    
    if (record.status === "paid") {
      return null;
    }
    if (dias < 0) {
      return `Vencido há ${Math.abs(Math.floor(dias))} dias`;
    } else if (dias === 0) {
      return "Vence hoje";
    } else {
      return `Vence em ${Math.floor(dias)} dias`;
    }
  };

  const getRowStyle = (record) => {
    const hoje = moment(moment()).format("DD/MM/yyyy");
    const vencimento = moment(record.dueDate).format("DD/MM/yyyy");
    var diff = moment(vencimento, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"));
    var dias = moment.duration(diff).asDays();
    if (dias < 0 && record.status !== "paid") {
      return { bgcolor: "rgba(239, 68, 68, 0.04)" };
    }
    return {};
  };

  return (
    <Box sx={{ minHeight: "100vh", py: { xs: 3, md: 4 }, px: { xs: 2, md: 3 }, bgcolor: "#f8fafc" }}>
      <SubscriptionModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        Invoice={storagePlans}
        contactId={selectedContactId}
      />

      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              bgcolor: PRIMARY_MAIN,
              "& svg": { width: 28, height: 28, color: "#fff" }
            }}
          >
            <ReceiptIcon />
          </Avatar>
          <Box>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: "text.primary",
                mb: 0.5,
                letterSpacing: "-0.5px",
                fontSize: { xs: "1.5rem", md: "1.875rem" }
              }}
            >
              Faturas ({invoices.length})
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
              Gerencie suas faturas e pagamentos
            </Typography>
          </Box>
        </Stack>

        {/* Cards móveis */}
        <Box sx={{ display: { xs: "block", md: "none" }, mb: 3 }}>
          <Grid container spacing={2}>
            {invoices.map((invoice) => {
              const statusInfo = getInvoiceStatus(invoice);
              return (
                <Grid item xs={12} key={invoice.id}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      bgcolor: "#fff",
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                      p: 2
                    }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight={700}>
                          {companyPlan.name}
                        </Typography>
                        <Chip
                          icon={statusInfo.icon}
                          label={statusInfo.text}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            bgcolor: statusInfo.bgcolor,
                            color: statusInfo.color,
                            border: `1px solid ${statusInfo.color}30`
                          }}
                        />
                      </Stack>

                      <Divider />

                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography variant="caption">{companyPlan.users} usuários</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <DevicesIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography variant="caption">{companyPlan.connections} conexões</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <QueueIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography variant="caption">{companyPlan.queues} filas</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <DateRangeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography variant="caption">{moment(invoice.dueDate).format("DD/MM/YYYY")}</Typography>
                          </Stack>
                        </Grid>
                      </Grid>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={700} color="primary">
                          {companyPlan.amount 
                            ? parseFloat(companyPlan.amount).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })
                            : invoice.value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                        </Typography>
                        {statusInfo.text !== "Pago" ? (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PaymentIcon />}
                            onClick={() => handleOpenContactModal(invoice)}
                            sx={{
                              bgcolor: PRIMARY_MAIN,
                              color: "#fff",
                              fontSize: "0.75rem",
                              textTransform: "none",
                              "&:hover": { bgcolor: PRIMARY_DARK }
                            }}
                          >
                            Pagar
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CheckCircleIcon />}
                            sx={{
                              fontSize: "0.75rem",
                              textTransform: "none",
                              color: "#10b981",
                              borderColor: "#10b981"
                            }}
                          >
                            Pago
                          </Button>
                        )}
                      </Stack>
                      {renderDaysLeft(invoice) && (
                        <Typography variant="caption" color="text.secondary">
                          {renderDaysLeft(invoice)}
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Tabela desktop */}
        <Paper
          elevation={0}
          onScroll={handleScroll}
          sx={{
            display: { xs: "none", md: "block" },
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
              Todas as Faturas
            </Typography>
          </Box>

          <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    Detalhes
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    <Tooltip title="Número de usuários">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <PersonIcon sx={{ fontSize: 14 }} />
                        <span>Usuários</span>
                      </Stack>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    <Tooltip title="Número de conexões">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <DevicesIcon sx={{ fontSize: 14 }} />
                        <span>Conexões</span>
                      </Stack>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    <Tooltip title="Número de filas">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <QueueIcon sx={{ fontSize: 14 }} />
                        <span>Filas</span>
                      </Stack>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    <Tooltip title="Valor da fatura">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <MoneyIcon sx={{ fontSize: 14 }} />
                        <span>Valor</span>
                      </Stack>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    <Tooltip title="Data de vencimento">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <DateRangeIcon sx={{ fontSize: 14 }} />
                        <span>Vencimento</span>
                      </Stack>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    Status
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    Ação
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => {
                  const statusInfo = getInvoiceStatus(invoice);
                  return (
                    <TableRow
                      key={invoice.id}
                      sx={{
                        ...getRowStyle(invoice),
                        "&:hover": { bgcolor: "action.hover", transition: "background-color 0.2s" }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                          {companyPlan.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                          {companyPlan.users}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                          {companyPlan.connections}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                          {companyPlan.queues}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.875rem", color: PRIMARY_MAIN }}>
                          {companyPlan.amount 
                            ? parseFloat(companyPlan.amount).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })
                            : invoice.value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack alignItems="center">
                          <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                            {moment(invoice.dueDate).format("DD/MM/YYYY")}
                          </Typography>
                          {renderDaysLeft(invoice) && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                              {renderDaysLeft(invoice)}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={statusInfo.icon}
                          label={statusInfo.text}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            bgcolor: statusInfo.bgcolor,
                            color: statusInfo.color,
                            border: `1px solid ${statusInfo.color}30`
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {statusInfo.text !== "Pago" ? (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PaymentIcon />}
                            onClick={() => handleOpenContactModal(invoice)}
                            sx={{
                              bgcolor: PRIMARY_MAIN,
                              color: "#fff",
                              fontSize: "0.75rem",
                              textTransform: "none",
                              "&:hover": { bgcolor: PRIMARY_DARK }
                            }}
                          >
                            Pagar
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CheckCircleIcon />}
                            sx={{
                              fontSize: "0.75rem",
                              textTransform: "none",
                              color: "#10b981",
                              borderColor: "#10b981"
                            }}
                          >
                            Pago
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {loading && <TableRowSkeleton columns={8} />}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </Box>
  );
};

export default Invoices;